import type { PayloadAction } from "@reduxjs/toolkit"
import { createAppSlice } from "@/app/createAppSlice"
import type {
  SlicePostSimpleResponse,
  PostTagResponse,
  BoardResponse,
  PostSimpleResponse,
} from "@/types/domain"
import { 
    getPosts, 
    getPostTags, 
    createPost as createPostApi, 
    getBoards, 
    uploadCommunityImage,
    type PostCreateRequest 
} from "@/services/api"
import { AxiosError } from "axios"

interface PostsState {
  posts: PostSimpleResponse[]
  tags: PostTagResponse[]
  boards: BoardResponse[]
  status: "idle" | "loading" | "succeeded" | "failed"
  error: string | null
  currentTags: string[]
  lastId?: string | undefined // For cursor-based pagination
  hasMore: boolean
}

const initialState: PostsState = {
  posts: [],
  tags: [],
  boards: [],
  status: "idle",
  error: null,
  currentTags: [],
  lastId: undefined,
  hasMore: true,
}

export const postsSlice = createAppSlice({
  name: "posts",
  initialState,
  reducers: create => ({
    setTags: create.reducer((state, action: PayloadAction<string[]>) => {
      state.currentTags = action.payload
      state.posts = []
      state.lastId = undefined
      state.hasMore = true
      state.status = "idle"
    }),
    resetPosts: create.reducer(state => {
      state.posts = []
      state.lastId = undefined
      state.hasMore = true
      state.status = "idle"
    }),
    fetchPosts: create.asyncThunk(
      async ({ tags, lastId }: { tags?: string[]; lastId?: string }, { rejectWithValue }) => {
        try {
          // Pass undefined if lastId is 0 to fetch from start, usually logic handles this
          const response = await getPosts(tags, lastId, 10)
          return response
        } catch (err) {
            if (err instanceof AxiosError && err.response) {
                return rejectWithValue(err.response.data ?? "Failed to fetch posts")
            }
            return rejectWithValue("Failed to fetch posts")
        }
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action: PayloadAction<SlicePostSimpleResponse>) => {
          state.status = "succeeded"
          const newPosts = action.payload.content
          if (!state.lastId) {
            // First fetch or reset
            state.posts = newPosts
          } else {
            // Append
            const existingIds = new Set(state.posts.map(p => p.id))
            const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id))
            state.posts = [...state.posts, ...uniqueNewPosts]
          }
          
          state.hasMore = !action.payload.last
          if (newPosts.length > 0) {
             // Set lastId to the id of the last element for next cursor
             state.lastId = newPosts[newPosts.length - 1].id
          }
        },
        rejected: (state, action) => {
          state.status = "failed"
          state.error = action.payload as string
        },
      }
    ),
    fetchTags: create.asyncThunk(
      async (_, { rejectWithValue }) => {
        try {
          const response = await getPostTags()
          return response
        } catch (err) {
            if (err instanceof AxiosError && err.response) {
                return rejectWithValue(err.response.data ?? "Failed to fetch tags")
            }
            return rejectWithValue("Failed to fetch tags")
        }
      },
      {
        fulfilled: (state, action) => {
          state.tags = action.payload
        },
      }
    ),
    fetchBoards: create.asyncThunk(
        async (_, { rejectWithValue }) => {
            try {
                const response = await getBoards()
                return response
            } catch (err) {
                return rejectWithValue("Failed to fetch boards")
            }
        },
        {
            fulfilled: (state, action) => {
                state.boards = action.payload
            }
        }
    ),
    createPost: create.asyncThunk(
        async ({ 
            title, 
            content, 
            tagNames,
            images,
            imageIds: providedImageIds,
            place
        }: { 
            title: string, 
            content: string, 
            tagNames: string[],
            images: File[],
            imageIds?: number[],
            place: any 
        }, { rejectWithValue }) => {
            try {
                // 2. Upload Images if imageIds not provided
                let finalImageIds: number[] = providedImageIds || [];
                
                if ((!providedImageIds || providedImageIds.length === 0) && images && images.length > 0) {
                    // Upload sequentially to avoid overwhelming server or implementation complexity
                    for (const file of images) {
                        const metadata = await uploadCommunityImage(file);
                        finalImageIds.push(metadata.imageId);
                    }
                }

                // 3. Create Post
                const request: PostCreateRequest = {
                    title: title,
                    content: content,
                    tagNames: tagNames, 
                    imageIds: finalImageIds,
                    campingSiteIds: place?.campingSiteId ? [String(place.campingSiteId)] : []
                }

                await createPostApi(request)
                return true
            } catch (err) {
                 if (err instanceof AxiosError && err.response) {
                    return rejectWithValue((err.response.data as string) ?? "Failed to create post")
                 }
                 return rejectWithValue("Failed to create post")
            }
        },
        {
            pending: (state) => {
                state.status = "loading";
            },
            fulfilled: (state) => {
                state.status = "idle"; // Reset to idle to trigger refetch in PostList
                state.lastId = undefined; 
                state.posts = []; 
                state.hasMore = true;
            },
            rejected: (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            }
        }
    ),
  }),
  selectors: {
      selectAllPosts: state => state.posts,
      selectPostsStatus: state => state.status,
      selectPostsError: state => state.error,
      selectTags: state => state.tags,
      selectCurrentTags: state => state.currentTags,
      selectHasMore: state => state.hasMore,
      selectBoards: state => state.boards,
      selectLastId: state => state.lastId,
  }
})

export const { setTags, resetPosts, fetchPosts, fetchTags, createPost, fetchBoards } = postsSlice.actions

export const {
    selectAllPosts,
    selectPostsStatus,
    selectPostsError,
    selectTags,
    selectCurrentTags,
    selectHasMore,
    selectBoards,
    selectLastId
} = postsSlice.selectors

export default postsSlice.reducer
